'use client';

import { Controller } from 'react-hook-form';

import { FormFieldLabel } from '@/features/original-plus/components/OriginalPlusApplicationForm/form-field-label';
import {
  applicationDescriptionClassName,
  applicationFootnoteClassName,
  checkboxClassName,
  checkboxGroupErrorClassName,
  checkboxLabelClassName,
  fieldErrorClassName,
  formInputClassName,
  inputErrorClassName,
  levelFieldTitleClassName,
  levelOptionClassName,
  submitButtonClassName,
} from '@/features/original-plus/components/OriginalPlusApplicationForm/original-plus-application-form.style';
import { OriginalPlusApplicationSuccessModal } from '@/features/original-plus/components/original-plus-application-success-modal';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import {
  applicationFormConsultationLevelId,
  applicationFormConsultationOption,
  originalPlusApplicationFieldId,
  originalPlusApplicationFormId,
  originalPlusApplicationRulesHref,
  sectionDisplayTitleClassName,
  sectionScrollMarginClass,
  type ApplicationFormLevelId,
} from '@/features/original-plus/constants';
import { useOriginalPlusApplicationForm } from '@/features/original-plus/hooks/use-original-plus-application-form';
import { cn } from '@/lib/utils';

export function OriginalPlusApplicationForm() {
  const {
    form,
    applicationFormLevelLabels,
    authorLevels,
    isSubmitting,
    submitError,
    successOpen,
    handleLevelChange,
    onSubmit,
    handleSuccessOpenChange,
  } = useOriginalPlusApplicationForm();

  const {
    register,
    control,
    formState: { errors, isSubmitted },
  } = form;

  const pseudonymFieldId = originalPlusApplicationFieldId('pseudonym');
  const contactFieldId = originalPlusApplicationFieldId('contact');
  const workLinkFieldId = originalPlusApplicationFieldId('work-link');

  const checkboxGroupHasError =
    isSubmitted &&
    Boolean(errors.personalDataAccepted || errors.rightsConfirmed || errors.rulesAccepted);

  return (
    <section
      id={originalPlusApplicationFormId}
      className={`mt-[100px] w-full md:mt-[220px] ${sectionScrollMarginClass}`}
      aria-labelledby="original-plus-application-form-title"
    >
      <div className="text-center">
        <h2
          id="original-plus-application-form-title"
          className={`text-center ${sectionDisplayTitleClassName}`}
        >
          Хочешь расти вместе с Molwe?
        </h2>

        <p className={`mx-auto mt-6 max-w-[720px] text-center ${applicationDescriptionClassName}`}>
          Оставь заявку — команда посмотрит текст и предложит подходящий формат участия. Фанфики и
          ориджиналы рассматриваются на равных.
        </p>

        <p className={`mt-1 ${applicationFootnoteClassName}`}>
          Это не массовая публикация. Это программа для авторов с потенциалом роста.
        </p>
      </div>

      <form className="mt-8 w-full md:mt-12" onSubmit={onSubmit} noValidate>
        <div className="flex flex-col gap-6 md:mx-auto md:w-[568px] md:rounded-[10px] md:bg-[#1B1D22] md:p-8">
          <div className="flex flex-col gap-2">
            <FormFieldLabel htmlFor={pseudonymFieldId} required>
              Псевдоним / Имя для публикации
            </FormFieldLabel>
            <Input
              id={pseudonymFieldId}
              autoComplete="nickname"
              placeholder="Как тебя называть?"
              aria-invalid={Boolean(errors.pseudonym)}
              aria-describedby={errors.pseudonym ? `${pseudonymFieldId}-error` : undefined}
              error={Boolean(errors.pseudonym)}
              className={cn(formInputClassName, errors.pseudonym && inputErrorClassName)}
              {...register('pseudonym')}
            />
            {errors.pseudonym ? (
              <p id={`${pseudonymFieldId}-error`} className={fieldErrorClassName} role="alert">
                {errors.pseudonym.message}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-2">
            <FormFieldLabel htmlFor={contactFieldId} required>
              Контакт для связи
            </FormFieldLabel>
            <Input
              id={contactFieldId}
              autoComplete="tel"
              placeholder="Telegram , VK, номер телефона"
              aria-invalid={Boolean(errors.contact)}
              aria-describedby={errors.contact ? `${contactFieldId}-error` : undefined}
              error={Boolean(errors.contact)}
              className={cn(formInputClassName, errors.contact && inputErrorClassName)}
              {...register('contact')}
            />
            {errors.contact ? (
              <p id={`${contactFieldId}-error`} className={fieldErrorClassName} role="alert">
                {errors.contact.message}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-2">
            <FormFieldLabel htmlFor={workLinkFieldId}>
              Ссылка на произведение или файл
            </FormFieldLabel>
            <Input
              id={workLinkFieldId}
              type="url"
              placeholder="https://molwe.ru/... (необязательно)"
              className={formInputClassName}
              {...register('workLink')}
            />
          </div>

          <fieldset className="mt-1 flex flex-col gap-3 border-0 p-0">
            <legend className={`${levelFieldTitleClassName} mb-1 w-full`}>
              Что хочешь получить от программы?
            </legend>
            <Controller
              name="levelId"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    handleLevelChange(value as ApplicationFormLevelId);
                  }}
                  className="grid grid-cols-1 gap-2 md:grid-cols-2"
                >
                  {authorLevels.map((level) => (
                    <label
                      key={level.id}
                      htmlFor={originalPlusApplicationFieldId(`level-${level.id}`)}
                      className={cn(levelOptionClassName, 'h-[58px] md:h-[66px]')}
                    >
                      <RadioGroupItem
                        id={originalPlusApplicationFieldId(`level-${level.id}`)}
                        value={level.id}
                        className="sr-only"
                      />
                      {applicationFormLevelLabels[level.id]}
                    </label>
                  ))}
                  <label
                    htmlFor={originalPlusApplicationFieldId(
                      `level-${applicationFormConsultationLevelId}`,
                    )}
                    className={cn(levelOptionClassName, 'h-[58px] md:col-span-2 md:h-[66px]')}
                  >
                    <RadioGroupItem
                      id={originalPlusApplicationFieldId(
                        `level-${applicationFormConsultationLevelId}`,
                      )}
                      value={applicationFormConsultationLevelId}
                      className="sr-only"
                    />
                    {applicationFormConsultationOption.title}
                  </label>
                </RadioGroup>
              )}
            />
          </fieldset>

          <div
            className={cn(
              'flex flex-col gap-3',
              checkboxGroupHasError && checkboxGroupErrorClassName,
            )}
            id="original-plus-application-form-checkboxes"
          >
            <div className="flex flex-col gap-1">
              <div className="flex items-start gap-3">
                <Controller
                  name="personalDataAccepted"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id={originalPlusApplicationFieldId('personal-data')}
                      checked={field.value}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                      aria-invalid={Boolean(errors.personalDataAccepted)}
                      className={checkboxClassName}
                    />
                  )}
                />
                <label
                  htmlFor={originalPlusApplicationFieldId('personal-data')}
                  className={checkboxLabelClassName}
                >
                  Согласен на обработку персональных данных
                </label>
              </div>
              {errors.personalDataAccepted ? (
                <p className={cn(fieldErrorClassName, 'pl-7')} role="alert">
                  {errors.personalDataAccepted.message}
                </p>
              ) : null}
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-start gap-3">
                <Controller
                  name="rightsConfirmed"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id={originalPlusApplicationFieldId('rights')}
                      checked={field.value}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                      aria-invalid={Boolean(errors.rightsConfirmed)}
                      className={checkboxClassName}
                    />
                  )}
                />
                <label
                  htmlFor={originalPlusApplicationFieldId('rights')}
                  className={checkboxLabelClassName}
                >
                  Подтверждаю, что обладаю правами на отправляемое произведение
                </label>
              </div>
              {errors.rightsConfirmed ? (
                <p className={cn(fieldErrorClassName, 'pl-7')} role="alert">
                  {errors.rightsConfirmed.message}
                </p>
              ) : null}
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-start gap-3">
                <Controller
                  name="rulesAccepted"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id={originalPlusApplicationFieldId('rules')}
                      checked={field.value}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                      aria-invalid={Boolean(errors.rulesAccepted)}
                      className={checkboxClassName}
                    />
                  )}
                />
                <label
                  htmlFor={originalPlusApplicationFieldId('rules')}
                  className={checkboxLabelClassName}
                >
                  Согласен с{' '}
                  <a
                    href={originalPlusApplicationRulesHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#A8EA3A] underline-offset-2 hover:underline"
                  >
                    правилами рассмотрения заявки
                  </a>
                </label>
              </div>
              {errors.rulesAccepted ? (
                <p className={cn(fieldErrorClassName, 'pl-7')} role="alert">
                  {errors.rulesAccepted.message}
                </p>
              ) : null}
            </div>
          </div>

          <div className="h-[3px] w-full bg-[#373737]" role="presentation" />

          {submitError ? (
            <p className={fieldErrorClassName} role="alert">
              {submitError}
            </p>
          ) : null}

          <button type="submit" disabled={isSubmitting} className={submitButtonClassName}>
            {isSubmitting ? 'Отправка…' : 'Подать заявку'}
          </button>
        </div>
      </form>

      <OriginalPlusApplicationSuccessModal
        open={successOpen}
        onOpenChange={handleSuccessOpenChange}
      />
    </section>
  );
}
